import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { trpc } from '@/lib/trpc'
import { Modal } from '../shared/modal'

const RESOURCE_TYPE_OPTIONS = ['video', 'article', 'course', 'book', 'tool']

interface EditResourceModalProps {
  isOpen: boolean
  onClose: () => void
  resource: {
    id: string
    title: string
    url: string
    type: string
    description: string | null
    icon: string | null
  }
  onResourceUpdated: (updated: {
    id: string
    title: string
    url: string
    type: string
    description: string | null
    icon: string | null
  }) => void
}

export function EditResourceModal({
  isOpen,
  onClose,
  resource,
  onResourceUpdated,
}: EditResourceModalProps) {
  const [title, setTitle] = useState(resource.title)
  const [url, setUrl] = useState(resource.url)
  const [type, setType] = useState(resource.type)
  const [description, setDescription] = useState(resource.description ?? '')
  const [icon, setIcon] = useState(resource.icon ?? '')
  const [error, setError] = useState('')

  const updateMutation = trpc.admin.resources.update.useMutation()

  // Reset form when resource changes
  useEffect(() => {
    setTitle(resource.title)
    setUrl(resource.url)
    setType(resource.type)
    setDescription(resource.description ?? '')
    setIcon(resource.icon ?? '')
    setError('')
  }, [resource])

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setError('Title is required')
      return
    }
    if (!url.trim()) {
      setError('URL is required')
      return
    }
    if (!type) {
      setError('Type is required')
      return
    }

    setError('')

    try {
      const updated = await updateMutation.mutateAsync({
        id: resource.id,
        title: title.trim(),
        url: url.trim(),
        type,
        description: description.trim() || null,
        icon: icon.trim() || null,
      })
      onResourceUpdated({
        id: updated.id,
        title: updated.title,
        url: updated.url,
        type: updated.type,
        description: updated.description,
        icon: updated.icon,
      })
      toast.success('Resource updated')
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update'
      setError(msg)
      toast.error(msg)
    }
  }, [
    title,
    url,
    type,
    description,
    icon,
    resource.id,
    updateMutation,
    onResourceUpdated,
    onClose,
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Resource">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="edit-resource-title"
            className="text-foreground mb-1 block text-sm font-medium"
          >
            Title
          </label>
          <Input
            id="edit-resource-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Resource title"
          />
        </div>

        <div>
          <label
            htmlFor="edit-resource-url"
            className="text-foreground mb-1 block text-sm font-medium"
          >
            URL
          </label>
          <Input
            id="edit-resource-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div>
          <label
            htmlFor="edit-resource-type"
            className="text-foreground mb-1 block text-sm font-medium"
          >
            Type
          </label>
          <select
            id="edit-resource-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border-input bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm"
          >
            {RESOURCE_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="edit-resource-description"
            className="text-foreground mb-1 block text-sm font-medium"
          >
            Description{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </label>
          <Textarea
            id="edit-resource-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description..."
            rows={2}
          />
        </div>

        <div>
          <label
            htmlFor="edit-resource-icon"
            className="text-foreground mb-1 block text-sm font-medium"
          >
            Icon{' '}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </label>
          <Input
            id="edit-resource-icon"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder="Icon identifier"
          />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
